/* eslint-disable react-hooks/set-state-in-effect */
/**
 * ProductGenerateImageDialog V2
 *
 * Dialog zur Auswahl eines KI-Referenzbildes für die Generierung.
 *
 * Drei Fälle:
 * 1. Keine KI-Referenzbilder → Hinweis + Button zum Zuordnen
 * 2. Ein Bild → Wird automatisch ausgewählt (Dialog wird nicht gezeigt)
 * 3. Mehrere Bilder → Auswahl-Grid
 */
import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Images, Wand2, Loader2, AlertCircle } from 'lucide-react';
import * as Dialog from '@radix-ui/react-dialog';
import { getAIReferenceImages, type AIRefSlot } from '@/lib/api/products';
import type { Product } from '@/types/products';
import { Button, Card, CardContent, Alert, AlertDescription } from '@/components/v2/ui';
import { cn } from '@/lib/utils';

export interface ProductGenerateImageDialogProps {
  show: boolean;
  product: Product | null;
  onClose: () => void;
  onOpenProductModal: (product: Product, tab: string) => void;
}

type DialogState = 'loading' | 'no_images' | 'select' | 'navigating';

export function ProductGenerateImageDialog({
  show,
  product,
  onClose,
  onOpenProductModal,
}: ProductGenerateImageDialogProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [state, setState] = useState<DialogState>('loading');
  const [slots, setSlots] = useState<AIRefSlot[]>([]);
  const [selectedSlot, setSelectedSlot] = useState<AIRefSlot | null>(null);
  const [error, setError] = useState<string | null>(null);

  const navigateToGeneration = useCallback((slot: AIRefSlot) => {
    if (!product) return;

    setState('navigating');

    const params = new URLSearchParams({
      productId: product.id,
      tab: 'product',
      productImageSlot: String(slot.slot),
    });

    navigate(`/v2/tools/studio/single-shot?${params.toString()}`);
    onClose();
  }, [product, navigate, onClose]);

  const loadAIReferences = useCallback(async () => {
    if (!product) return;

    setState('loading');
    setError(null);

    try {
      const response = await getAIReferenceImages(product.id);

      if (response.success) {
        const filledSlots = response.slots.filter((s) => !s.is_empty);
        setSlots(filledSlots);

        if (filledSlots.length === 0) {
          setState('no_images');
        } else if (filledSlots.length === 1) {
          navigateToGeneration(filledSlots[0]);
        } else {
          setState('select');
        }
      } else {
        setError('Fehler beim Laden der KI-Referenzbilder');
        setState('no_images');
      }
    } catch (err) {
      console.error('Error loading AI references:', err);
      setError('Fehler beim Laden der KI-Referenzbilder');
      setState('no_images');
    }
  }, [product, navigateToGeneration]);

  // Lade KI-Referenzbilder wenn Dialog geöffnet wird
  useEffect(() => {
    if (show && product) {
      loadAIReferences();
    }
  }, [show, product, loadAIReferences]);

  const handleSelectAndNavigate = () => {
    if (selectedSlot) {
      navigateToGeneration(selectedSlot);
    }
  };

  const handleOpenProductForAssignment = () => {
    if (product) {
      onClose();
      onOpenProductModal(product, 'ai-references');
    }
  };

  if (!show) return null;

  return (
    <Dialog.Root open={show} onOpenChange={(open) => !open && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/70 z-50" />
        <Dialog.Content
          className={cn(
            'fixed z-50 top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2',
            'w-full max-w-md',
            'bg-white dark:bg-neutral-900 rounded-xl shadow-xl',
            'focus:outline-none'
          )}
        >
          {/* Header */}
          <div className="px-6 py-4 border-b border-neutral-200 dark:border-neutral-700">
            <Dialog.Title className="flex items-center gap-2 text-lg font-semibold text-neutral-900 dark:text-white">
              <Wand2 className="h-5 w-5 text-primary-500" />
              Bild generieren
            </Dialog.Title>
          </div>

          {/* Content */}
          <div className="px-6 py-6">
            {/* Loading */}
            {state === 'loading' && (
              <div className="text-center py-8">
                <Loader2 className="h-8 w-8 animate-spin text-primary-500 mx-auto mb-3" />
                <p className="text-neutral-600 dark:text-neutral-400">
                  Lade KI-Referenzbilder...
                </p>
              </div>
            )}

            {/* Navigating */}
            {state === 'navigating' && (
              <div className="text-center py-8">
                <Loader2 className="h-8 w-8 animate-spin text-success-500 mx-auto mb-3" />
                <p className="text-neutral-600 dark:text-neutral-400">
                  {t('products.openImageGenerator')}
                </p>
              </div>
            )}

            {/* Error */}
            {error && (
              <Alert variant="error" className="mb-4">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            {/* Fall 1: Keine Bilder */}
            {state === 'no_images' && !error && (
              <div className="text-center py-4">
                <Images className="h-12 w-12 text-neutral-400 mx-auto mb-4" />
                <h3 className="font-semibold text-neutral-900 dark:text-white mb-2">
                  Keine KI-Referenzbilder zugeordnet
                </h3>
                <p className="text-sm text-neutral-500 dark:text-neutral-400 mb-6">
                  {t('products.assignReferenceFirst')}
                </p>
                <Button onClick={handleOpenProductForAssignment}>
                  KI-Referenzbild zuordnen
                </Button>
              </div>
            )}

            {/* Fall 3: Mehrere Bilder → Auswahl */}
            {state === 'select' && (
              <>
                <p className="text-sm text-neutral-600 dark:text-neutral-400 mb-4">
                  <span className="font-semibold text-neutral-900 dark:text-white">
                    {product?.name}
                  </span>{' '}
                  {t('products.multipleAiReferences')}
                </p>

                <div className="grid grid-cols-2 gap-3">
                  {slots.map((slot) => (
                    <Card
                      key={slot.slot}
                      className={cn(
                        'cursor-pointer transition-all',
                        selectedSlot?.slot === slot.slot
                          ? 'ring-2 ring-primary-500 bg-primary-50 dark:bg-primary-900/20'
                          : 'hover:bg-neutral-50 dark:hover:bg-neutral-800'
                      )}
                      onClick={() => setSelectedSlot(slot)}
                    >
                      <CardContent className="p-3 text-center">
                        <div className="w-20 h-20 mx-auto mb-2 bg-neutral-200 dark:bg-neutral-700 rounded-lg overflow-hidden">
                          {slot.thumbnail_url ? (
                            <img
                              src={slot.thumbnail_url}
                              alt={`Slot ${slot.slot}`}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <Images className="h-6 w-6 text-neutral-400" />
                            </div>
                          )}
                        </div>
                        <div className="flex justify-between items-center text-xs">
                          <span className="text-neutral-500">Slot {slot.slot}</span>
                          {slot.role && (
                            <span className="px-1.5 py-0.5 bg-neutral-100 dark:bg-neutral-700 rounded text-neutral-600 dark:text-neutral-400">
                              {slot.role}
                            </span>
                          )}
                        </div>
                        {slot.width && slot.height && (
                          <div className="text-xs text-neutral-400 mt-1">
                            {slot.width}×{slot.height}px
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </>
            )}
          </div>

          {/* Footer für Auswahl-Modus */}
          {state === 'select' && (
            <div className="px-6 py-4 border-t border-neutral-200 dark:border-neutral-700 flex justify-end gap-3">
              <Button variant="secondary" onClick={onClose}>
                {t('common.cancel')}
              </Button>
              <Button onClick={handleSelectAndNavigate} disabled={!selectedSlot}>
                <Wand2 className="h-4 w-4 mr-2" />
                Mit diesem Bild generieren
              </Button>
            </div>
          )}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
